import React, { useState, useEffect, useRef } from 'react';

import { Card, TableHead, TableBody, Table, TableRow, TableCell } from '@material-ui/core';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import Skeleton from '@material-ui/lab/Skeleton';
import { openReorderConfirmationModalAction } from 'actions/reorderConfirmationActions';
import { SkeletonContainer, StyledTextField } from 'components/ObjectiveTagList/styles';
import { useDispatch } from 'react-redux';
import { CheckRadio } from 'screens/home/tasksList/styles';
import styled, { css } from 'styled-components';
import {
  Divider1,
  FlexContainer,
  NewStyledIconButton,
  PaddingCSS,
  StyledBox,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { makeCommaSeparatedString } from 'constants/helper';
import { ChevronDownIcon, ChevronUpIcon, UncheckedCircle } from 'constants/icons';
import theme from 'constants/theme';

import { reorderOrganizationGrades } from 'services/admin';

import {
  getLeftPadding,
  getLeftPosition,
  horizontalLineWidth,
  PlayIcon,
  PlayIconContainer,
  StyledVerticalLine,
} from './FunctionAndGradeEditableRow';

const getVerticalLines = (nestingLevel, levelsForWhichLinesToBeDrawn = []) => {
  if (nestingLevel === 0) return null;

  return (
    <>
      {levelsForWhichLinesToBeDrawn.map((num) => (
        <StyledVerticalLine left={`${getLeftPosition(nestingLevel + 1 - num)}`} />
      ))}
      <StyledVerticalLine left={`-${horizontalLineWidth}px`} bottom="50%" />
    </>
  );
};

const onMoveUpHandler = (currentGrade, prevGrade, grades, refetchData = () => {}) => {
  const { displayOrder: currentGradeDisplayOrder } = currentGrade?.grade ?? {};
  const { displayOrder: prevGradeDisplayOrder } = prevGrade?.grade ?? {};
  let modifiedGrades = [...grades];
  modifiedGrades.splice(currentGradeDisplayOrder - 1, 1);
  modifiedGrades.splice(prevGradeDisplayOrder - 1, 0, currentGrade?.grade);
  modifiedGrades = modifiedGrades.map((g, idx) => ({ ...g, displayOrder: idx + 1 }));
  reorderOrganizationGrades(modifiedGrades).then((res) => refetchData());
};

const onMoveDownHandler = (currentGrade, nextGrade, grades, refetchData = () => {}) => {
  const { displayOrder: currentGradeDisplayOrder } = currentGrade?.grade ?? {};
  const { displayOrder: nextGradeDisplayOrder } = nextGrade?.grade ?? {};
  let modifiedGrades = [...grades];
  modifiedGrades.splice(nextGradeDisplayOrder - 1, 1);
  modifiedGrades.splice(currentGradeDisplayOrder - 1, 0, nextGrade?.grade);
  modifiedGrades = modifiedGrades.map((g, idx) => ({ ...g, displayOrder: idx + 1 }));
  reorderOrganizationGrades(modifiedGrades).then((res) => refetchData());
};

const gradeReorderHandler =
  (
    type,
    {
      gradeValue: currentGrade,
      nextGradeValue: nextGrade,
      gradesForOrg: grades,
      prevGradeValue: prevGrade,
      refetchData,
    }
  ) =>
  () => {
    if (type === 'DOWN') {
      onMoveDownHandler(currentGrade, nextGrade, grades, refetchData);
    } else {
      onMoveUpHandler(currentGrade, prevGrade, grades, refetchData);
    }
  };

const RoleRow = ({
  modalView,
  showBorder,
  children,
  onSelect,
  roleValue,
  nestingLevel,
  levelsForWhichLinesToBeDrawn,
  showRoles,
  parentFunctionId,
  parentGradeId,
  jobRoleId,
  parentChecked,
  setShowRoles,
}) => {
  const checked = parentChecked && jobRoleId === +roleValue.role.uuid;

  useEffect(() => {
    setShowRoles(checked);
  }, []);

  if (!showRoles) {
    return null;
  }
  return (
    <StyledTableRow
      style={{
        borderBottom: showBorder ? `1px solid ${theme.palette.borders.lighterGrey}` : 'none',
      }}
    >
      <StyledTableCell paddingTop="0" paddingBottom="0" paddingLeft={getLeftPadding(nestingLevel)} display="flex">
        <FlexContainer paddingLeft="0">{getVerticalLines(nestingLevel, levelsForWhichLinesToBeDrawn)}</FlexContainer>
      </StyledTableCell>
      {/* <GradeContainer> */}
      <StyledTableCell paddingTop="0" paddingBottom="0" paddingLeft="0" display="flex">
        <FlexContainer
          paddingLeft="0"
          style={{
            borderBottom: showBorder ? `1px solid ${theme.palette.borders.lighterGrey}` : 'none',
          }}
        >
          {children}
          <TextFieldContainer>
            <StyledTextField
              disabled
              name="name"
              placeholder="Name"
              fullWidth
              variant="outlined"
              value={roleValue.role.name}
              style={{ paddingLeft: 0 }}
            />
          </TextFieldContainer>
          {/* <StyledCircularOutlineIcon color="primary" icon={ChevronUpIcon} /> */}
          {modalView && (
            <CheckRadio
              disableRipple
              checked={checked}
              style={{ marginLeft: 'auto', padding: 0 }}
              checkedIcon={<UncheckedCircle size="smallest" color={theme.palette.primary.main} />}
              icon={<UncheckedCircle size="smallest" color="textSecondary" />}
              hoverColor="textSecondary"
              onClick={() => {
                onSelect({
                  jobFunctionId: +parentFunctionId,
                  jobGradeUI: +parentGradeId,
                  jobRoleId: +roleValue.role.uuid,
                });
              }}
            />
          )}
        </FlexContainer>
      </StyledTableCell>
      {/* </GradeContainer> */}
    </StyledTableRow>
  );
};

const GradeRoleRow = ({
  nestingLevel,
  gradeValue,
  children,
  isEditing = false,
  gradesForOrg,
  nextGradeValue,
  prevGradeValue,
  refetchData,
  showBorder,
  levelsForWhichLinesToBeDrawn,
  parentArrayOfFunction = [],
  jobFunctionId,
  jobGradeUI,
  jobRoleId,
  modalView,
  onSelect,
  parentTag,
}) => {
  const [showRoles, setShowRoles] = useState(false);
  const dispatch = useDispatch();
  const rolesCommaSeparated = gradeValue.roles
    ? makeCommaSeparatedString(gradeValue.roles.map((r) => r.role.name))
    : null;

  const gradeRef = useRef(null);

  const checked = parentTag.function.uuid === String(jobFunctionId) && gradeValue.grade.uuid === String(jobGradeUI);

  useEffect(() => {
    if (checked) {
      if (gradeRef?.current) {
        gradeRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'start',
        });
      }
    }
  }, [gradeRef]);
  return (
    <>
      <StyledTableRow
        style={{
          borderBottom: showBorder ? `1px solid ${theme.palette.borders.lighterGrey}` : 'none',
        }}
        ref={gradeRef}
      >
        <StyledTableCell
          paddingTop="0"
          paddingBottom="0"
          paddingLeft={getLeftPadding(nestingLevel)}
          display="flex"
          width="40%"
        >
          <FlexContainer paddingLeft="0">{getVerticalLines(nestingLevel, levelsForWhichLinesToBeDrawn)}</FlexContainer>
        </StyledTableCell>
        {/* <GradeContainer> */}
        {/* <VerticallyAlignedFlexContainer
          paddingLeft="0"
          style={{
            borderBottom: showBorder ? `1px solid ${theme.palette.borders.lighterGrey}` : 'none'
          }}
        > */}
        <StyledTableCell paddingTop="0" paddingBottom="0" display="flex" width="30%">
          {children}
          {modalView && (
            <NewStyledIconButton
              rightGutter={1}
              padding="none"
              height="max-content"
              onClick={() => setShowRoles((prev) => !prev)}
              color="primary"
            >
              <PlayIconContainer>
                <PlayIcon rotate={showRoles} />
              </PlayIconContainer>
            </NewStyledIconButton>
          )}
          <TextFieldContainer isEditing={isEditing}>
            <StyledTextField
              disabled={!isEditing}
              name="name"
              placeholder="Name"
              fullWidth
              variant="outlined"
              value={gradeValue.grade.name}
              style={{ paddingLeft: 0 }}
              //   onKeyDown={handleKeyDown}
              //   onChange={handleTagChange}
              //   autoFocus={getFocus}
            />
          </TextFieldContainer>
        </StyledTableCell>

        <StyledTableCell paddingTop="0" paddingBottom="0" display="flex" width="auto">
          {!modalView && (
            <TextFieldContainer isEditing={isEditing}>
              <StyledTextField
                disabled={!isEditing}
                name="name"
                placeholder="No roles available"
                fullWidth
                variant="outlined"
                value={rolesCommaSeparated ?? 'No roles available'}
                style={{ paddingLeft: 0 }}
                //   onKeyDown={handleKeyDown}
                //   onChange={handleTagChange}
                //   autoFocus={getFocus}
              />
            </TextFieldContainer>
          )}
        </StyledTableCell>
        <StyledTableCell paddingTop="0" paddingBottom="0" display="flex" width="auto">
          <VerticallyAlignedFlexContainer>
            {!modalView ? (
              <StyledIconContainer>
                {prevGradeValue && (
                  <ChevronUpIcon
                    size="fixed10px"
                    containerSize="fixed24px"
                    circularOutline
                    color="primary"
                    bgColor={theme.palette.background.paper}
                    style={{ cursor: 'pointer', marginRight: '1rem' }}
                    onClick={() => {
                      dispatch(
                        openReorderConfirmationModalAction({
                          onSelect: gradeReorderHandler('UP', {
                            gradeValue,
                            prevGradeValue,
                            gradesForOrg,
                            refetchData,
                          }),
                        })
                      );
                    }}
                  />
                )}
                {nextGradeValue && (
                  <ChevronDownIcon
                    size="fixed10px"
                    containerSize="fixed24px"
                    circularOutline
                    color="primary"
                    bgColor={theme.palette.background.paper}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      dispatch(
                        openReorderConfirmationModalAction({
                          onSelect: gradeReorderHandler('DOWN', {
                            gradeValue,
                            nextGradeValue,
                            gradesForOrg,
                            refetchData,
                          }),
                        })
                      );
                    }}
                  />
                )}
              </StyledIconContainer>
            ) : (
              <CheckRadio
                disableRipple
                checked={checked}
                style={{ marginLeft: 'auto', padding: 0 }}
                checkedIcon={<UncheckedCircle size="smallest" color={theme.palette.primary.main} />}
                icon={<UncheckedCircle size="smallest" color="textSecondary" />}
                hoverColor="textSecondary"
                onClick={() => {
                  onSelect({
                    jobFunctionId: +parentTag.function.uuid,
                    jobGradeUI: +gradeValue.grade.uuid,
                  });
                }}
              />
            )}
          </VerticallyAlignedFlexContainer>
        </StyledTableCell>

        {/* </GradeContainer> */}
      </StyledTableRow>
      {modalView &&
        gradeValue.roles.map((role) => (
          <RoleRow
            modalView={modalView}
            onSelect={onSelect}
            roleValue={role}
            nestingLevel={nestingLevel}
            levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
            showRoles={showRoles}
            parentFunctionId={parentTag.function.uuid}
            parentGradeId={gradeValue.grade.uuid}
            jobRoleId={jobRoleId}
            parentChecked={checked}
            setShowRoles={setShowRoles}
          />
        ))}
    </>
  );
};

const GradeAndRoleRow = ({
  parentTag,
  enableParentExpansion,
  disableParentExpansion,
  nestingLevel = 1,
  showExpandedCard,
  modalView,
  enableCreation,
  selectTag,
  levelsForWhichLinesToBeDrawn,
  editingTags,
  setEditingTags,
  grades,
  gradesForOrg,
  refetchData,
  parentArrayOfFunction = [],
  jobFunctionId,
  jobGradeUI,
  jobRoleId,
  onSelect,
}) => (
  <StyledTableCell border-bottom="none" padding="none" colSpan={5}>
    <Table>
      {/* <TableHeader /> */}
      <TableBody>
        {showExpandedCard &&
          grades?.map((grade, index) => (
            <GradeRoleRow
              key={`grade-role-row-${index}`}
              gradeValue={grade}
              nextGradeValue={grades[index + 1]}
              prevGradeValue={grades[index - 1]}
              gradesForOrg={gradesForOrg}
              refetchData={refetchData}
              showBorder={grades.length - 1 !== index}
              nestingLevel={nestingLevel}
              levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
              parentArrayOfFunction={parentArrayOfFunction}
              jobFunctionId={jobFunctionId}
              jobGradeUI={jobGradeUI}
              jobRoleId={jobRoleId}
              modalView={modalView}
              onSelect={onSelect}
              parentTag={parentTag}
            />
          ))}
      </TableBody>
    </Table>
    <Divider1 />
  </StyledTableCell>
);

const Loader = () => (
  <SkeletonContainer>
    <Skeleton width="50%" />
    <Skeleton width="40%" />
  </SkeletonContainer>
);

const TableHeader = () => (
  <TableHead>
    {/* <HeaderTableCell padding="0" width="50%"></HeaderTableCell> */}
    {/* <HeaderTableCell padding="0" width="50%"></HeaderTableCell> */}
    {/* <HeaderTableCell padding="0" width="25%" />
      <HeaderTableCell padding="0" width="10%"></HeaderTableCell> */}
  </TableHead>
);

const StyledCard = styled(Card)`
  width: 100%;
  background-color: ${(props) => props.bgColor};
  box-shadow: none;
  overflow: visible;
  table thead th {
    border-bottom: 0;
  }
`;

const StyledCellContainer = styled.div`
  position: relative;
`;

const StyledTypography2 = styled(StyledTypography)`
  line-height: 3;
  font-size: 0.875rem;
`;

const StyledAddIcon = styled(AddRoundedIcon)`
  border-radius: 50%;
  fill: ${(props) => props.theme.palette.primary.main};
  border: ${(props) => `1px solid ${props.theme.palette.primary.main}`};
  padding: 2px;
`;

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 40px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 0px;


  
  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${props.theme.palette.background.paper};
      border: 1px solid ${props.theme.palette.borders.lighterGrey};
      border-radius: 5px;
    `}
    input {
      padding-left 12px;
    }
`;
const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;

  visibility: hidden;
`;
const StyledTableRow = styled(TableRow)`
  ${(props) =>
    props.modalView &&
    !props.isEditing &&
    css`
      td * {
        cursor: pointer;
      }
    `};

  ${(props) =>
    props.isEditing &&
    css`
      background-color: #e9f6ff;
    `}

  &:hover {
    background-color: #e9f6ff;
    ${StyledIconContainer} {
      visibility: visible;
    }
  }
`;
const GradeContainer = styled.div`
  &:hover {
    background-color: #e9f6ff;
    ${StyledIconContainer} {
      visibility: visible;
    }
  }
`;
const FullWidthBox = styled(StyledBox)`
  width: 100%;
`;
const StyledTableCell = styled(TableCell)`
  position: relative;
  width: ${(props) => props.width || 'auto'};
  text-align: ${(props) => props.textAlign || 'left'};
  ${PaddingCSS};
  border-bottom: ${(props) => (props.border ? `1px solid #EAEAEA` : 'none')};
  ${(props) =>
    props.compact &&
    css`
      padding: 0.5em 0;
      line-height: 0;
    `}
`;

export default GradeAndRoleRow;
